import api from "./api";

/**
 * Reference catalogues used when designing a floor layout: what sector a
 * building belongs to, and what each space on a floor is for. Both are shared
 * reference data, so there is no company scoping.
 */

const asArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeBuildingType = (dto) => ({
  building_type_id: dto?.building_type_id ?? dto?.id,
  code: dto?.code ?? "",
  name: dto?.name ?? dto?.code ?? "",
  description: dto?.description ?? "",
});

const normalizeSpaceType = (dto) => ({
  space_type_id: dto?.space_type_id ?? dto?.id,
  code: dto?.code ?? "",
  name: dto?.name ?? dto?.code ?? "",
  category: dto?.category ?? "",
  is_circulation: Boolean(dto?.is_circulation),
  default_capacity: dto?.default_capacity ?? null,
});

const normalizeRoomTemplateItem = (dto) => ({
  room_template_item_id: dto?.room_template_item_id ?? dto?.id ?? null,
  asset: dto?.asset ?? "",
  x: dto?.x ?? 0,
  z: dto?.z ?? 0,
  rotation_deg: dto?.rotation_deg ?? 0,
  scale: dto?.scale ?? 1,
  seats: dto?.seats ?? 0,
});

const normalizeRoomTemplate = (dto) => ({
  room_template_id: dto?.room_template_id ?? dto?.id,
  code: dto?.code ?? "",
  name: dto?.name ?? dto?.code ?? "",
  space_type_id: dto?.space_type_id ?? null,
  row_span: Number(dto?.row_span) || 1,
  col_span: Number(dto?.col_span) || 1,
  is_default: Boolean(dto?.is_default),
  description: dto?.description ?? "",
  item: Array.isArray(dto?.item) ? dto.item.map(normalizeRoomTemplateItem) : [],
});

export async function getBuildingTypes() {
  const data = await api.get("/building-type");
  return asArray(data).map(normalizeBuildingType);
}

export async function getSpaceTypes() {
  const data = await api.get("/space-type");
  return asArray(data).map(normalizeSpaceType);
}

/**
 * The furniture-layout catalogue a room's space type resolves against. Same
 * shared reference data as space types: no company scoping, safe to load once
 * per designer session.
 */
export async function getRoomTemplates() {
  const data = await api.get("/room-template");
  return asArray(data).map(normalizeRoomTemplate);
}

/**
 * Which space-type categories to offer for a building sector. Everything gets
 * the shared "common" spaces plus the ones specific to its sector, so a
 * hospital picker is not cluttered with warehouse bays.
 */
export const CATEGORY_FOR_BUILDING_TYPE = {
  OFFICE: "office",
  UNIVERSITY: "education",
  SCHOOL: "education",
  HOSPITAL: "healthcare",
  CLINIC: "healthcare",
  BANK: "finance",
  RETAIL: "retail",
  WAREHOUSE: "industrial",
  FACTORY: "industrial",
  HOTEL: "hospitality",
};

/**
 * The building type a `with-relations` payload carries, as `{ code, name }`.
 *
 * Read from the nested object the endpoint already expands, never looked up
 * against `/building-type`. The catalogue is only needed to OFFER a type — on
 * the Building form — and fetching all of it on every floor page to translate
 * one id into one code is a request for something the payload came with.
 *
 * Null when the building has no type, which the caller shows the whole
 * catalogue for: a floor still has to be designable before anyone has said what
 * kind of building it is in.
 */
export function buildingTypeOf(building) {
  const type = building?.building_type ?? building?.buildingType ?? null;
  const code = String(type?.code ?? "")
    .trim()
    .toUpperCase();
  if (!code) return null;
  return { code, name: type.name || code };
}

export function spaceTypesForBuilding(spaceTypes, buildingTypeCode) {
  const sector = CATEGORY_FOR_BUILDING_TYPE[buildingTypeCode];
  // MIXED (or an unset building type) sees the whole catalogue.
  if (!sector) return spaceTypes;
  return spaceTypes.filter((s) => s.category === "common" || s.category === sector);
}

/**
 * The space types a ROOM may be used as.
 *
 * Circulation spaces (corridor, lobby, …) are excluded deliberately. On this
 * plan, whether a cell is circulation is its ROLE — the cellType painted with
 * the palette — not a purpose given to a room. Offering them here as well let
 * one cell be a Room *used as* a Corridor: two systems describing the same
 * thing, which the validator (which reads the role, to check the walk route)
 * and the furniture resolver (which reads the space type) then disagree about.
 *
 * `is_circulation` comes from the catalogue itself, so this needs no list of
 * names to keep in step.
 */
export function roomSpaceTypes(spaceTypes, buildingTypeCode) {
  return spaceTypesForBuilding(spaceTypes, buildingTypeCode).filter(
    (s) => !s.is_circulation,
  );
}

/**
 * Which template items are real desks — the ones a job gets assigned to, as
 * opposed to a chair around a meeting or boardroom table. Matched by asset
 * name (the kit's individual workstations are `desk_1`/`desk_2`/`desk_3`).
 * Everything else may still carry its own `seats` for the room's display
 * total (a meeting room's six chairs, say), but it is not a workstation and
 * never becomes a real `table` row — which is also why the seat cap (which
 * governs real desks only) is never troubled by a boardroom template that
 * deliberately seats more people than 4-per-cell would otherwise allow.
 */
const DESK_ASSET_RE = /^desk/i;
export const isDeskAsset = (asset) => DESK_ASSET_RE.test(String(asset || ""));

/**
 * The desk seat-count a resolved template calls for, one entry per
 * desk-bearing item — the target `table.capacity` list a room's real tables
 * should reconcile to.
 */
export function deskSeatSlots(template) {
  if (!template) return [];
  return (template.item ?? [])
    .filter((it) => isDeskAsset(it.asset) && Number(it.seats) > 0)
    .map((it) => Number(it.seats) || 1);
}
